import { fbt } from 'fbtee'
import { cn } from '../../../lib/utils'

// The "now" line + dot inside the schedule grid. Each layer is a grid item that
// spans every column (gridColumn: 1 / -1), so it stretches the grid's FULL
// scrollable width rather than just the visible viewport. A plain
// absolute/left-0/right-0 child resolves against the grid's padding box (the
// viewport width, since the grid is `w-full`), so the line was cut off across
// the scrolled-away region whenever the columns overflowed horizontally.
//
// Lives inside the scrolling grid so the browser scrolls it natively (no JS, no
// lag). The dot is sticky-left so it stays anchored to the left edge while the
// line scrolls horizontally.
//
// When `draggable` is set (a clock override is active), the dot becomes a grab
// handle: dragging it vertically re-times the override. A time pill rides
// alongside so the user sees the target time while dragging.
export function NowIndicator({
  top,
  draggable = false,
  dragging = false,
  timeLabel,
  onHandlePointerDown,
}: {
  top: number
  draggable?: boolean
  dragging?: boolean
  timeLabel?: string
  onHandlePointerDown?: (e: React.PointerEvent) => void
}) {
  // Full-grid layer: all columns, all body rows (row 1 is the sticky header).
  // The inner row is placed at the now-line's pixel offset within the body.
  const layer: React.CSSProperties = {
    gridColumn: '1 / -1',
    gridRow: '2 / -1',
    position: 'relative',
  }
  const row: React.CSSProperties = {
    position: 'absolute',
    top: `${top}px`,
    left: 0,
    right: 0,
    transform: 'translateY(-50%)',
    // Glide between positions while the clock is overridden (the festival-mode
    // preview / sim advances the time in steps) so the line moves smoothly rather
    // than jumping. Off during a drag (follow the pointer) and on live time (the
    // line barely moves minute-to-minute, and we don't want a load-time slide).
    transition: draggable && !dragging ? 'top 200ms linear' : undefined,
  }
  return (
    <>
      <div className="pointer-events-none" style={{ ...layer, zIndex: 20 }}>
        <div className="flex items-center" style={row}>
          <div className="shrink-0 w-screen" style={{ position: 'sticky', left: 0 }}>
            <div className={cn('ml-2 h-1 rounded-full', dragging ? 'bg-accent' : 'bg-accent/70')} />
          </div>
        </div>
      </div>
      <div className="pointer-events-none" style={{ ...layer, zIndex: 35 }}>
        <div className="flex items-center" style={row}>
          <div className="flex items-center shrink-0" style={{ position: 'sticky', left: 0 }}>
            <div className="w-1" />
            {draggable ? (
              <div
                role="slider"
                aria-label={fbt(
                  'Drag to change the simulated time',
                  'Accessible label for the draggable now-line handle in festival schedule',
                )}
                onPointerDown={onHandlePointerDown}
                className="pointer-events-auto touch-none cursor-grab active:cursor-grabbing flex items-center gap-1 -m-2 p-2"
              >
                <div className="relative">
                  {!dragging && (
                    <div className="absolute inset-0 rounded-full bg-accent animate-ping opacity-75" />
                  )}
                  <div className="w-2.5 h-2.5 rounded-full bg-accent relative ring-2 ring-accent/30" />
                </div>
                {timeLabel && (
                  <span className="text-[10px] leading-none font-semibold tabular-nums bg-accent text-accent-foreground rounded px-1 py-0.5 shadow-sm">
                    {timeLabel}
                  </span>
                )}
              </div>
            ) : (
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-accent animate-ping opacity-75" />
                <div className="w-2 h-2 rounded-full bg-accent relative" />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
